import { useEffect, useState, useCallback, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  Plus,
  MessageSquare,
  UserCheck,
  Building2,
  Archive,
  Users,
  Phone,
  User,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { DeleteModal } from "../../components/modals/DeleteModal"
import { callApi } from "../../services/callApi"
import AssignmentModal from "../../components/modals/AssignmentModal"
import SmsTemplateModal from "../../components/modals/SmsTemplateModal"
import { Spinner } from "../../components/common/Spinner"
import { ControlsBar } from "../../components/controls/LeadControlBar"
import { Tooltip } from "../../components/ui/tooltip"
import { PaginationControls } from "../../components/common/PaginationControls"
import { leadsApi, Lead } from "../../services/leadsApi"
import { attorneysApi, Attorney, FilterableAttorney } from "../../services/attorneysApi"
import { firmsApi, Firm } from "../../services/firmsApi"
import { Toast } from "../../components/ui/toast"
import { formatTimeAgo as formatTimeAgoUtil } from "../../utils/format-time"
import {
  enrichLeads,
  filterLeadsForDemo,
  isPhase2DemoEnabled,
  sortLeadsForDemo,
} from "../../demo/phase2Demo"
import { StoplightBadge } from "../../components/leads/StoplightBadge"
import { SeveranceBadge } from "../../components/leads/SeveranceBadge"
import { BulkActionsBar } from "../../components/leads/BulkActionsBar"

// Custom hook for leads data management
const useLeadsData = (
  statusFilter: string,
  searchQuery: string,
  sourceFilter: string,
  currentPage: number,
  recordsPerPage: number
) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [leads, setLeads] = useState<Lead[]>([])
  const [leadsCount, setLeadsCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const apiSearch = isPhase2DemoEnabled() ? '' : searchQuery
      const response = await leadsApi.getLeads(currentPage, recordsPerPage, apiSearch, {
        status: statusFilter,
        ...(sourceFilter && !isPhase2DemoEnabled() ? { source: sourceFilter } : {}),
      })

      let processed = enrichLeads(response.leads)
      if (isPhase2DemoEnabled()) {
        processed = filterLeadsForDemo(processed, { search: searchQuery, source: sourceFilter })
        processed = sortLeadsForDemo(processed)
      }

      setLeads(processed)
      setLeadsCount(isPhase2DemoEnabled() ? processed.length : response.total_count)
      // Ensure totalPages is at least 1, even when there are no leads
      setTotalPages(Math.max(1, Math.ceil(response.total_count / recordsPerPage)))
    } catch (err) {
      console.error('Failed to fetch leads:', err)
      setError('Failed to load leads. Please try again.')
      setLeads([])
      setLeadsCount(0)
      setTotalPages(1) // Set to 1 instead of 0 when there's an error
    } finally {
      setLoading(false)
    }
  }, [currentPage, recordsPerPage, searchQuery, sourceFilter, statusFilter])

  useEffect(() => {
    fetchLeads()
  }, [fetchLeads])

  return {
    loading,
    error,
    leads,
    leadsCount,
    totalPages,
    refetch: fetchLeads
  }
}

// Custom hook for URL state management with localStorage fallback
const useUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Get values from URL params with localStorage fallback for status
  const urlStatus = searchParams.get('status')
  const localStorageStatus = localStorage.getItem('leadsStatusFilter')
  
  // Use URL status if available, otherwise fall back to localStorage, then default to 'new'
  const statusFilter = urlStatus || localStorageStatus || 'new'
  const currentPage = parseInt(searchParams.get('page') || '1')
  const searchQuery = searchParams.get('search') || ''
  const recordsPerPage = parseInt(searchParams.get('perPage') || '50')
  const sourceFilter = searchParams.get('source') || ''
  
  // Ensure URL always has a status parameter and sync with localStorage
  useEffect(() => {
    const currentStatus = searchParams.get('status')
    if (!currentStatus) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('status', statusFilter)
      setSearchParams(newParams, { replace: true })
    }
    // Also update localStorage to keep it in sync
    localStorage.setItem('leadsStatusFilter', statusFilter)
  }, [searchParams, setSearchParams, statusFilter])
  
  const updateUrlState = useCallback((updates: Record<string, string | number>) => {
    const newParams = new URLSearchParams(searchParams)
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        newParams.delete(key)
      } else {
        newParams.set(key, String(value))
      }
    })
    
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])
  
  return {
    statusFilter,
    currentPage,
    searchQuery,
    recordsPerPage,
    sourceFilter,
    updateUrlState
  }
}

export default function Leads() {
  const navigate = useNavigate()
  
  // URL state management
  const { statusFilter, currentPage, searchQuery, recordsPerPage, sourceFilter, updateUrlState } =
    useUrlState()
  

  
  // Local state
  const [searchInputValue, setSearchInputValue] = useState(searchQuery)
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
  
  // Delete modal states
  const [deleteLeadModal, setDeleteLeadModal] = useState(false)
  const [selectedId, setSelectedId] = useState<string>("")
  
  // Toast states
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  // Assignment states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [assignmentType, setAssignmentType] = useState<"attorney" | "firm" | null>(null)
  const [attorneys, setAttorneys] = useState<Attorney[]>([])
  const [firms, setFirms] = useState<Firm[]>([])
  const [selectedLeadId, setSelectedLeadId] = useState("")

  // SMS modal states
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [selectedLeadForSms, setSelectedLeadForSms] = useState<Lead | null>(null)
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(() => new Set())
  const [bulkAssignment, setBulkAssignment] = useState(false)

  // Filterable attorneys state (for dynamic empty state messages)
  const [filterableAttorneys, setFilterableAttorneys] = useState<FilterableAttorney[]>([])

  // Custom hook for leads data
  const { loading, error, leads, leadsCount, totalPages, refetch } = useLeadsData(
    statusFilter,
    searchQuery,
    sourceFilter,
    currentPage,
    recordsPerPage
  )

  // Sync search input with URL state
  useEffect(() => {
    setSearchInputValue(searchQuery)
  }, [searchQuery])

  // Load filterable attorneys on mount for empty state messages
  useEffect(() => {
    const loadFilterableAttorneys = async () => {
      try {
        const response = await attorneysApi.getFilterableAttorneys()
        if (!response.error && response.attorneys) {
          setFilterableAttorneys(response.attorneys)
        }
      } catch (error) {
        console.error('Failed to load filterable attorneys:', error)
      }
    }
    loadFilterableAttorneys()
  }, [])

  // Memoized handlers to prevent unnecessary re-renders
  const handleStatusChange = useCallback((status: string) => {
    updateUrlState({ status, page: 1 }) // Reset to first page when changing status
    // Also update localStorage as backup
    localStorage.setItem('leadsStatusFilter', status)
  }, [updateUrlState])

  const handleSourceChange = useCallback(
    (source: string) => {
      updateUrlState({ source, page: 1 })
    },
    [updateUrlState]
  )

  const toggleLeadSelection = useCallback((leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev)
      if (next.has(leadId)) next.delete(leadId)
      else next.add(leadId)
      return next
    })
  }, [])

  const clearSelection = useCallback(() => setSelectedLeadIds(new Set()), [])

  const handleSearchSubmit = useCallback((query: string) => {
    updateUrlState({ search: query, page: 1 }) // Reset to first page when searching
  }, [updateUrlState])

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInputValue(value)
  }, [])

  const handleRecordsPerPage = useCallback((value: string) => {
    const newValue = Number.parseInt(value)
    updateUrlState({ perPage: newValue, page: 1 })
  }, [updateUrlState])

  const handlePreviousPage = useCallback(() => {
    const newPage = Math.max(currentPage - 1, 1)
    updateUrlState({ page: newPage })
  }, [currentPage, updateUrlState])

  const handleNextPage = useCallback(() => {
    const newPage = Math.min(currentPage + 1, totalPages)
    updateUrlState({ page: newPage })
  }, [currentPage, totalPages, updateUrlState])

  const onAddHandle = useCallback(() => {
    if (!loading) {
      navigate("/app/leads/add-leads")
    }
  }, [loading, navigate])

  const selectLeadList = useCallback((leadId: string) => {
    navigate(`/app/leads/lead-details-modern`, {
      state: { leadId, detail: true },
    })
  }, [navigate])


  const deleteLeadModalClose = useCallback(() => {
    setDeleteLeadModal(false)
    setSelectedId("")
  }, [])

  const deleteItem = useCallback(async () => {
    try {
      await leadsApi.deleteLead(selectedId)
      deleteLeadModalClose()
      refetch() // Refresh the leads list
    } catch (err) {
      console.error('Failed to delete lead:', err)
      setToastMessage('Failed to delete lead. Please try again.')
      setToastType('error')
      setShowToast(true)
    }
  }, [selectedId, deleteLeadModalClose, refetch])

  const handleArchiveLead = useCallback(async (leadId: string, status: string) => {
    try {
      if(status === 'archived'){
        // Unarchive the lead by updating its status
        // Find the lead in the current list to determine appropriate status
        const lead = leads.find(l => l.id === leadId)
        let newStatus = "new" // default status
        
        if (lead?.assigned_to_attorney) {
          newStatus = "assigned"
        } else if (lead?.referred_to_firm) {
          newStatus = "referred"
        }
        
        await leadsApi.updateLead(leadId, { status: newStatus })
        setToastMessage("Lead unarchived successfully!")
      } else {
        await leadsApi.archiveLead(leadId)
        setToastMessage("Lead archived successfully!")
      }
      
      setToastType('success')
      setShowToast(true)
      refetch() // Refresh the list
    } catch (err) {
      console.error('Failed to archive/unarchive lead:', err)
      setToastMessage("Failed to archive/unarchive lead. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }, [refetch, leads])

  // Memoized utility functions
  const toggleNotes = useCallback((leadId: string) => {
    setExpandedNotes(prev => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(leadId)) {
        newExpanded.delete(leadId)
      } else {
        newExpanded.add(leadId)
      }
      return newExpanded
    })
  }, [])

  const truncateText = useCallback((text: string, maxLength = 60) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + "..."
  }, [])

  const formatTimeAgo = useCallback((dateString: string) => {
    return formatTimeAgoUtil(dateString)
  }, [])

  const formatCommentDisplay = useCallback((lead: Lead) => {
    if (!lead.last_comment) return "No notes available"
    
    const comment = lead.last_comment.comment
    const author = lead.last_comment.commented_by_display
    const date = new Date(lead.last_comment.commented_on)
    const timeAgo = formatTimeAgo(date.toISOString())
    
    // Format comment as [...] TEXT [...] without first and last sentence
    const formatCommentText = (text: string) => {
      // Split by sentence endings (., !, ?)
      const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0)
      
      if (sentences.length <= 2) {
        // If 2 or fewer sentences, show the middle content or the whole text
        return `[...] ${text.trim()} [...]`
      }
      
      // Remove first and last sentence, keep the middle ones
      const middleSentences = sentences.slice(1, -1)
      const middleText = middleSentences.join('. ').trim()
      
      return middleText ? `[...] ${middleText} [...]` : `[...] ${text.trim()} [...]`
    }
    
    const formattedComment = formatCommentText(comment)
    return `${formattedComment} — ${author} • ${timeAgo}`
  }, [formatTimeAgo])

  // Memoized records list
  const recordsList = useMemo(() => [
    { value: "10", label: "10 Records per page" },
    { value: "20", label: "20 Records per page" },
    { value: "30", label: "30 Records per page" },
    { value: "40", label: "40 Records per page" },
    { value: "50", label: "50 Records per page" },
    { value: "100", label: "100 Records per page" },
  ], [])

  // Memoized empty state content
  const emptyStateContent = useMemo(() => {
    if (searchQuery) {
      return {
        title: 'No leads found matching your search',
        description: 'Try adjusting your search terms.',
        showAddButton: false
      }
    }
    
    switch (statusFilter) {
      case 'new':
        return {
          title: 'No leads found',
          description: 'Get started by adding your first lead.',
          showAddButton: true
        }
      case 'assigned':
        return {
          title: 'No assigned leads found',
          description: 'Here you would see leads assigned to attorneys.',
          showAddButton: false
        }
      case 'referred':
        return {
          title: 'No referred leads found',
          description: 'Here you would see leads referred to firms.',
          showAddButton: false
        }
      case 'archived':
        return {
          title: 'No archived leads found',
          description: 'Here you would see archived leads.',
          showAddButton: false
        }
      default:
        // Handle dynamic "assigned_to_[attorney_name]" filters
        if (statusFilter.startsWith('assigned_to_')) {
          // Find the attorney from the filterable attorneys list
          const attorney = filterableAttorneys.find(
            a => a.filter_value === statusFilter
          )
          
          if (attorney) {
            return {
              title: `No leads assigned to ${attorney.first_name}`,
              description: `No leads assigned to ${attorney.name} at the moment.`,
              showAddButton: false
            }
          } else {
            // Fallback if attorney not found in list (shouldn't happen, but handle gracefully)
            const attorneyName = statusFilter.replace('assigned_to_', '')
            const capitalizedName = attorneyName.charAt(0).toUpperCase() + attorneyName.slice(1)
            return {
              title: `No leads assigned to ${capitalizedName}`,
              description: `No leads assigned to ${capitalizedName} at the moment.`,
              showAddButton: false
            }
          }
        }
        // Fallback for unknown status filters
        return {
          title: `No ${statusFilter} leads found`,
          description: 'No leads found for this status.',
          showAddButton: false
        }
    }
  }, [searchQuery, statusFilter, filterableAttorneys])

  // SMS handlers
  const handleSendSMS = useCallback((lead: Lead) => {
    setSelectedLeadForSms(lead)
    setShowSmsModal(true)
  }, [])

  const handleSmsSent = useCallback(
    async (leadId: string, _template: string, status: 'success' | 'error', message: string) => {
      if (status === 'success') {
        try {
          await leadsApi.archiveLead(leadId)
          setToastMessage('SMS sent. Lead archived.')
          setToastType('success')
        } catch {
          setToastMessage(message)
          setToastType('success')
        }
      } else {
        setToastMessage(message)
        setToastType('error')
      }
      setShowToast(true)
      refetch()
    },
    [refetch]
  )

  // Assignment functions
  const loadAttorneys = useCallback(async () => {
    try {
       try {
         const response = await attorneysApi.getAttorneys(1, 100)
         if (response.attorneys && response.attorneys.length > 0) {
           setAttorneys(response.attorneys)
         } 
       } catch (apiError) {
         console.log("API failed, using mock attorney data")
       }
    } catch (error) {
      console.error("Failed to load attorneys:", error)
      setToastMessage("Failed to load attorneys. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }, [])

  const loadFirms = useCallback(async () => {
    try {
      try {
        const response = await firmsApi.getFirms(1, 100)
        if (response.firms && response.firms.length > 0) {
          setFirms(response.firms)
        }
      } catch (apiError) {
        console.log("API failed, using mock firm data")
      }
    } catch (error) {
      console.error("Failed to load firms:", error)
      setToastMessage("Failed to load firms. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }, [])

  const handleAssignmentModalOpen = useCallback((type: "attorney" | "firm", leadId: string) => {
    setBulkAssignment(false)
    setAssignmentType(type)
    setSelectedLeadId(leadId)
    setShowAssignmentModal(true)
    
    if (type === "attorney") {
      if (attorneys.length === 0) {
        loadAttorneys()
      }
    } else if (type === "firm") {
      if (firms.length === 0) {
        loadFirms()
      }
    }
  }, [attorneys.length, firms.length, loadAttorneys, loadFirms])

  const handleAssignToAttorney = useCallback((leadId: string) => {
    handleAssignmentModalOpen("attorney", leadId)
  }, [handleAssignmentModalOpen])

  const handleAssignToReferralFirm = useCallback((leadId: string) => {
    handleAssignmentModalOpen("firm", leadId)
  }, [handleAssignmentModalOpen])

  const handleBulkArchive = useCallback(async () => {
    const ids = Array.from(selectedLeadIds)
    if (ids.length === 0) return
    let ok = 0
    for (const id of ids) {
      try {
        const lead = leads.find((l) => l.id === id)
        if (lead?.status === 'archived') {
          await leadsApi.updateLead(id, { status: 'new' })
        } else {
          await leadsApi.archiveLead(id)
        }
        ok++
      } catch (e) {
        console.error(e)
      }
    }
    setToastMessage(`Archived ${ok} of ${ids.length} lead(s).`)
    setToastType(ok === ids.length ? 'success' : 'error')
    setShowToast(true)
    clearSelection()
    refetch()
  }, [selectedLeadIds, leads, clearSelection, refetch])

  const openBulkAssign = useCallback(
    (type: 'attorney' | 'firm') => {
      if (selectedLeadIds.size === 0) return
      setBulkAssignment(true)
      setAssignmentType(type)
      setSelectedLeadId(Array.from(selectedLeadIds)[0] ?? '')
      setShowAssignmentModal(true)
      if (type === 'attorney' && attorneys.length === 0) loadAttorneys()
      if (type === 'firm' && firms.length === 0) loadFirms()
    },
    [selectedLeadIds, attorneys.length, firms.length, loadAttorneys, loadFirms]
  )

  const handlePhoneClick = useCallback(async (lead: Lead) => {
    if (!lead.phone) {
      setToastMessage('No phone number available for this lead')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      // Always use PSTN: Redirect call to agent phone
      setToastMessage('📞 Calling your phone...')
      setToastType('success')
      setShowToast(true)

      // Initiate PSTN call
      await callApi.makeCall(lead.phone)

      // Update toast on success
      setTimeout(() => {
        setToastMessage(`✓ Pick up your phone to connect to ${lead.name || 'lead'}`)
        setToastType('success')
        setShowToast(true)
      }, 1500)

      // Refresh leads list after a delay (call might be completed)
      setTimeout(() => {
        refetch()
      }, 5000)

    } catch (error: any) {
      setToastMessage(`Call failed: ${error.message || 'Unknown error'}`)
      setToastType('error')
      setShowToast(true)
    }
  }, [refetch])




  // Memoized LeadCard component
  const LeadCard = useCallback(({ lead }: { lead: Lead }) => {
    const isExpanded = expandedNotes.has(lead.id)
    const notes = formatCommentDisplay(lead)
    const shouldShowToggle = notes.length > 60

    const isSelected = selectedLeadIds.has(lead.id)

    return (
      <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
        <CardContent className="overflow-visible p-2">
          {/* Desktop Layout */}
          <div className="hidden md:flex min-h-8 items-center gap-3 min-w-0 overflow-visible">
            <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleLeadSelection(lead.id)}
                className="h-4 w-4 flex-shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                aria-label={`Select ${lead.name}`}
              />
              <div className="flex h-6 flex-shrink-0 items-center gap-1 overflow-visible w-[2.25rem]">
                <StoplightBadge score={lead.priority_score} reason={lead.score_reason} />
                <SeveranceBadge show={lead.is_severance} />
              </div>
              <div className="flex w-36 flex-shrink-0 items-center gap-2 min-w-0 lg:w-40">
                <User className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <h3
                  className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors"
                  onClick={() => selectLeadList(lead.id)}
                >
                  {lead.name || "Unknown"}
                </h3>
              </div>

              <div className="flex w-32 flex-shrink-0 items-center gap-2 min-w-0 lg:w-36">
                <Phone className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <button
                  onClick={() => handlePhoneClick(lead)}
                  className="min-w-0 flex-1 truncate text-left text-sm text-slate-700 hover:text-slate-900 hover:underline cursor-pointer"
                >
                  +{lead.phone}
                </button>
              </div>

              <div
                className="hidden lg:flex w-36 flex-shrink-0 items-center min-w-0 text-xs text-slate-500"
                title={lead.assigned_to_attorney?.name ?? undefined}
              >
                {lead.assigned_to_attorney ? (
                  <span className="truncate">→ {lead.assigned_to_attorney.name}</span>
                ) : (
                  <span className="text-slate-300" aria-hidden="true">—</span>
                )}
              </div>

              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <p
                  className="min-w-0 flex-1 truncate text-sm text-slate-600"
                  title={lead.last_comment?.comment || notes}
                >
                  {notes}
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-4 pl-1">
              <Tooltip content="Send SMS" side="top">
                <MessageSquare 
                  className="h-4 w-4 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                  onClick={() => handleSendSMS(lead)}
                />
              </Tooltip>
              <Tooltip content="Assign to attorney" side="top">
                <UserCheck 
                  className="h-4 w-4 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                  onClick={() => handleAssignToAttorney(lead.id)}
                />
              </Tooltip>
              <Tooltip content="Refer to firm" side="top">
                <Building2 
                  className="h-4 w-4 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                  onClick={() => handleAssignToReferralFirm(lead.id)}
                />
              </Tooltip>
              <Tooltip content={lead.status === 'archived' ? 'Unarchive' : 'Archive'} side="top">
                <Archive 
                  className="h-4 w-4 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                  onClick={() => handleArchiveLead(lead.id, lead.status || 'new')}
                />
              </Tooltip>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex justify-between items-start mb-3">
              {/* Name and Phone */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleLeadSelection(lead.id)}
                    className="h-4 w-4 rounded border-slate-300"
                    aria-label={`Select ${lead.name}`}
                  />
                  <div className="flex h-6 flex-shrink-0 items-center gap-1 overflow-visible w-[2.25rem]">
                    <StoplightBadge score={lead.priority_score} reason={lead.score_reason} />
                    <SeveranceBadge show={lead.is_severance} />
                  </div>
                  <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h3
                      className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                      onClick={() => selectLeadList(lead.id)}
                    >
                      {lead.name || "Unknown"}
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => handlePhoneClick(lead)}
                      className="text-sm text-slate-700 hover:text-slate-900 transition-colors truncate hover:underline cursor-pointer text-left"
                    >
                      +{lead.phone}
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Icons */}
              <div className="flex items-center gap-4">
                <Tooltip content="Send SMS" side="top">
                  <MessageSquare 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={() => handleSendSMS(lead)}
                  />
                </Tooltip>
                <Tooltip content="Assign to attorney" side="top">
                  <UserCheck 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={() => handleAssignToAttorney(lead.id)}
                  />
                </Tooltip>
                <Tooltip content="Refer to firm" side="top">
                  <Building2 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={() => handleAssignToReferralFirm(lead.id)}
                  />
                </Tooltip>
                <Tooltip content={lead.status === 'archived' ? 'Unarchive lead' : 'Archive lead'} side="top">
                  <Archive 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={() => handleArchiveLead(lead.id, lead.status || 'new')}
                  />
                </Tooltip>
              </div>
            </div>

            {/* Notes Section */}
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {isExpanded ? notes : truncateText(notes)}
                  </p>
                  {shouldShowToggle && (
                    <button
                      onClick={() => toggleNotes(lead.id)}
                      className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mt-1 transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="h-3 w-3" />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3" />
                          Show more
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }, [
    expandedNotes,
    formatCommentDisplay,
    handleArchiveLead,
    handleAssignToAttorney,
    handleAssignToReferralFirm,
    handlePhoneClick,
    handleSendSMS,
    selectLeadList,
    truncateText,
    toggleNotes,
    selectedLeadIds,
    toggleLeadSelection,
  ])

  return (
    <div className="min-h-screen bg-slate-50 pt-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Lead Management</h1>
          <p className="text-slate-600">Manage and track your legal leads efficiently</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Controls Bar */}
        <ControlsBar
          openLeadsCount={leadsCount}
          closedLeadsCount={0} // Since we don't have status field anymore
          recordsPerPage={recordsPerPage}
          onRecordsPerPageChange={handleRecordsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onAddLead={onAddHandle}
          recordsList={recordsList}
          searchQuery={searchInputValue}
          onSearchChange={handleSearchSubmit}
          onSearchInputChange={handleSearchInputChange}
          currentRecordsCount={leads.length}
          statusFilter={statusFilter}
          onStatusChange={handleStatusChange}
          sourceFilter={sourceFilter}
          onSourceChange={handleSourceChange}
        />

        {isPhase2DemoEnabled() && (
          <p className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Phase 2 demo mode — priority sort and extended search use sample data.
          </p>
        )}

        <BulkActionsBar
          selectedCount={selectedLeadIds.size}
          onClear={clearSelection}
          onBulkArchive={handleBulkArchive}
          onBulkAssign={() => openBulkAssign('attorney')}
          onBulkRefer={() => openBulkAssign('firm')}
        />

        {/* Leads Content */}
        <div className="space-y-2 md:space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : leads.length > 0 ? (
            leads.map((lead) => <LeadCard key={lead.id} lead={lead} />)
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <div className="text-slate-400 mb-4">
                  <Users className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {emptyStateContent.title}
                </h3>
                <p className="text-slate-600 mb-4">
                  {emptyStateContent.description}
                </p>
                                 {emptyStateContent.showAddButton && (
                  <Button onClick={onAddHandle} className="bg-slate-900 hover:bg-slate-800 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Lead
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />

        {/* Assignment Modal */}
        <AssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false)
            setBulkAssignment(false)
            setAssignmentType(null)
            setSelectedLeadId("")
          }}
          assignmentType={assignmentType}
          onAssignment={async (type, id) => {
            try {
              const targetIds = bulkAssignment
                ? Array.from(selectedLeadIds)
                : selectedLeadId
                  ? [selectedLeadId]
                  : []

              if (type === "attorney") {
                const attorney = attorneys.find(a => a.id === id)
                if (!attorney) {
                  setToastMessage("Attorney not found. Please try again.")
                  setToastType('error')
                  setShowToast(true)
                  return
                }

                for (const leadId of targetIds) {
                  await leadsApi.assignToAttorney(leadId, id)
                }

                setToastMessage(
                  targetIds.length > 1
                    ? `Assigned ${targetIds.length} leads to ${attorney.name}`
                    : `Lead assigned to attorney: ${attorney.name}`
                )
                setToastType('success')
                setShowToast(true)
                refetch()
              } else if (type === "firm") {
                const firm = firms.find(f => f.id === id)
                if (!firm) {
                  setToastMessage("Firm not found. Please try again.")
                  setToastType('error')
                  setShowToast(true)
                  return
                }

                for (const leadId of targetIds) {
                  await leadsApi.referToFirm(leadId, id)
                }

                setToastMessage(
                  targetIds.length > 1
                    ? `Referred ${targetIds.length} leads to ${firm.name}`
                    : `Lead referred to firm: ${firm.name}`
                )
                setToastType('success')
                setShowToast(true)
                refetch()
              }

              if (bulkAssignment) clearSelection()
              setShowAssignmentModal(false)
              setBulkAssignment(false)
              setAssignmentType(null)
              setSelectedLeadId("")
            } catch (error) {
              console.error("Failed to assign lead:", error)
              setToastMessage("Failed to assign lead. Please try again.")
              setToastType('error')
              setShowToast(true)
            }
          }}
          currentAssignment={(() => {
            const currentLead = leads.find(lead => lead.id === selectedLeadId)
            if (!currentLead) return null
            
            if (assignmentType === "attorney" && currentLead.assigned_to_attorney) {
              return {
                type: "attorney" as const,
                id: currentLead.assigned_to_attorney.id,
                name: currentLead.assigned_to_attorney.name
              }
            } else if (assignmentType === "firm" && currentLead.referred_to_firm) {
              return {
                type: "firm" as const,
                id: currentLead.referred_to_firm.id,
                name: currentLead.referred_to_firm.name
              }
            }
            return null
          })()}
          onRemoveAssignment={
            selectedLeadId
              ? async () => {
                  try {
                    await leadsApi.removeAssignment(selectedLeadId)
                    setToastMessage("Assignment removed successfully")
                    setToastType('success')
                    setShowToast(true)
                    // Refresh the leads list
                    refetch()
                  } catch (error) {
                    console.error("Failed to remove assignment:", error)
                    setToastMessage("Failed to remove assignment. Please try again.")
                    setToastType('error')
                    setShowToast(true)
                  }
                }
              : undefined
          }
        />

        {/* SMS Template Modal */}
        {selectedLeadForSms && (
          <SmsTemplateModal
            isOpen={showSmsModal}
            onClose={() => {
              setShowSmsModal(false)
              setSelectedLeadForSms(null)
            }}
            lead={selectedLeadForSms}
            onSmsSent={(template, status, message) =>
              handleSmsSent(selectedLeadForSms.id, template, status, message)
            }
          />
        )}


        <DeleteModal
          open={deleteLeadModal}
          onClose={deleteLeadModalClose}
          onConfirm={deleteItem}
          title="Delete Lead"
          description="Are you sure you want to delete this lead? This action cannot be undone."
        />

        {/* Toast Notification */}
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={showToast}
          onClose={() => setShowToast(false)}
        />
      </div>
    </div>
  )
}
